import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { OverviewAPI } from 'api';
import { listClients } from 'api/clientsApi';
import { JobTableType } from 'api/jobApi';
import { buildJobListUrl } from '../jobListUrlParams';
import DateRangeFilter from './DateRangeFilter';
import {
  cardSx,
  chartTokens,
  formatCount,
  subtitleSx,
  tabularNumSx,
  titleSx,
} from './dashboardChartTokens';

interface Props {
  label?: string;
  /** Limits the count to the jobs one coordinator runs. */
  coordinatorId?: string;
}

const JobsByClientChart = ({ label, coordinatorId }: Props) => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [rangeSummary, setRangeSummary] = useState('all time');

  const { isFetching, data } = useQuery(
    ['client-job-volume', startDate, endDate, coordinatorId],
    () => OverviewAPI.fetchClientJobVolume(startDate, endDate, coordinatorId)
  );

  // The job list's client filter matches legal names, while the volume
  // endpoint returns trading names — resolve the row's client id to its legal
  // name so the drill-through lands on the same client the row counts.
  const { data: clients } = useQuery(['list-clients'], listClients);

  /**
   * Drill-through to the client's jobs. The All tab is used because volume
   * counts every status; the range selector has no URL equivalent.
   */
  const drillToClientJobs = (clientId: string, companyName: string) => {
    const legalName =
      clients?.find((client) => client.id === clientId)?.legalName ??
      companyName;
    navigate(
      buildJobListUrl({
        tab: JobTableType.ALL,
        client: [legalName],
        coordinator: coordinatorId ? [coordinatorId] : undefined,
      })
    );
  };

  const rows = data?.clients ?? [];
  const scaleMax = Math.max(...rows.map((row) => row.jobs), 1);
  const clientTotal = data?.clientCount ?? rows.length;

  return (
    <Card sx={cardSx}>
      {isFetching && <LinearProgress />}
      <CardContent
        sx={{
          flex: 1,
          padding: { xs: 2.5, md: 3 },
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}>
          <Box>
            <Typography sx={titleSx}>
              {label ?? 'Top Clients by Volume'}
            </Typography>
            <Typography sx={subtitleSx}>
              {`Job count ${rangeSummary} · top ${rows.length} of ${clientTotal}`}
            </Typography>
          </Box>
          <DateRangeFilter
            onChange={(start, end, summary) => {
              setStartDate(start);
              setEndDate(end);
              setRangeSummary(summary);
            }}
          />
        </Box>

        <Divider sx={{ borderColor: chartTokens.divider, marginY: 2.5 }} />

        {isFetching && !rows.length ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} height={44} />
            ))}
          </Box>
        ) : !rows.length ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <img
              alt="blank canvas"
              src="/images/undraw_blank_canvas_3rbb.svg"
              style={{ width: '60%', maxWidth: 220 }}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
            {rows.map((row) => (
              <Box
                key={row.id}
                role="button"
                tabIndex={0}
                onClick={() => drillToClientJobs(row.id, row.companyName)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    drillToClientJobs(row.id, row.companyName);
                  }
                }}
                sx={{ cursor: 'pointer' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 2,
                    marginBottom: 0.75,
                  }}>
                  <Tooltip title={row.companyName}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 14.5,
                        fontWeight: 500,
                        color: chartTokens.heading,
                      }}>
                      {row.companyName}
                    </Typography>
                  </Tooltip>
                  <Typography
                    sx={{
                      flexShrink: 0,
                      fontSize: 14.5,
                      fontWeight: 700,
                      color: chartTokens.heading,
                      ...tabularNumSx,
                    }}>
                    {formatCount(row.jobs)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    height: 8,
                    borderRadius: '4px',
                    backgroundColor: chartTokens.track,
                    overflow: 'hidden',
                  }}>
                  <Box
                    sx={{
                      height: '100%',
                      width: `${(row.jobs / scaleMax) * 100}%`,
                      minWidth: row.jobs > 0 ? 4 : 0,
                      borderRadius: '4px',
                      backgroundColor: chartTokens.accent,
                      transition: 'width 300ms ease',
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default JobsByClientChart;
